import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { DSButton, DSText, DSSpacer } from '@zoe/ds-web'
import { ScreenContainer } from '../core/layout/ScreenContainer'
import { StickyFooter, StickyFooterContent } from '../core/layout/StickyFooter'
import { ContextAwareText } from '../core/text/ContextAwareText'
import { ZiggieAsk } from '../core/ziggie/ZiggieAsk'
import { GifName } from '../../data/gifs'
import { onNextClickType } from '../types'
import { QuizBase } from '../../redux/quiz/types'
import { Answer, submitAnswers } from '../../redux/quiz/slice'
import { AppDispatch } from '../../App'
import { trackQuizQuestionAnswered } from '../gtmV2'
import { getQuizRouteConfig } from '../versioned-components/v5/quizRouting'
import DEFAULT_QUIZ_CONFIG from '../../config/quiz.config'
import { VISITED_SYMPTOM_RESULTS_KEY } from './SymptomScoreCalculatingScreen'
import {
  calculateE7SymptomScore,
  getE7ScoreCategory,
  getE7TopSymptoms,
  E7ScoreCategory,
  E7Severity,
} from '../../libs/symptomScore/e7Utils'

// Body copy per score category (verbatim from Figma). Bold segment rendered via
// ContextAwareText's <b> handling.
const CATEGORY_BODY: Record<E7ScoreCategory, string> = {
  '50-100':
    "You're experiencing a <b>high level of symptom severity</b>, which is likely having a major impact on your day-to-day quality of life.",
  '21-49':
    "You're experiencing a <b>significant level of symptom severity</b>, which may be affecting your day-to-day life in ways worth addressing.",
  '0-20':
    "You're experiencing a <b>moderate level of symptom severity</b> which may be affecting your day-to-day quality of life in ways worth addressing.",
}

const SEVERITY_COLOURS: Record<E7Severity, { background: string; color: string }> = {
  Severe: { background: '#F2785C', color: '#2D3246' },
  Significant: { background: '#F2A93D', color: '#2D3246' },
  Moderate: { background: '#FFC83D', color: '#2D3246' },
}

const DISCLAIMER =
  'Your score is for general information only and is not medical advice. None of our products or services are intended to diagnose, manage or treat a medical condition.'

const ANIMATION_MS = 1500

const Card = styled.div`
  background: var(--colour-surface-cream-subtle);
  border-radius: 16px;
  padding: 24px 16px;
  text-align: center;
`

const Bar = styled.div`
  position: relative;
  width: 100%;
  height: 12px;
  border-radius: 9999px;
  background: var(--colour-surface-cream-bold);
  overflow: hidden;
`

// Coral fill grows from the left to the score position as the number counts up.
const BarFill = styled.div<{ $fraction: number }>`
  height: 100%;
  border-radius: 9999px;
  background: #f2785c;
  width: ${({ $fraction }) => `${$fraction * 100}%`};
  transition: width ${ANIMATION_MS}ms ease-out;
`

const BarLabelsRow = styled.div`
  display: flex;
  justify-content: space-between;
`

const SymptomCard = styled.div`
  display: flex;
  flex-direction: column;
  background: var(--colour-surface-cream-subtle);
  border-radius: 12px;
  padding: 16px;
  text-align: left;
`

const SymptomHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`

const CardDivider = styled.hr`
  border: none;
  border-top: 1px solid var(--colour-surface-cream-bold);
  margin: 12px 0;
`

const SeverityBadge = styled.span<{ $background: string; $color: string }>`
  display: inline-block;
  padding: 6px 14px;
  border-radius: 9999px;
  background: ${({ $background }) => $background};
  color: ${({ $color }) => $color};
  font-size: 14px;
  font-weight: 400;
  flex-shrink: 0;
`

export type SymptomScoreResultsScreenE7Props = {
  buttonText?: string
  // e12: 18px "Your key symptoms" and 16px symptom names, as in the design.
  compactKeySymptoms?: boolean
  // Ziggie reacts to the score from a speech bubble above the key symptoms, with
  // the message for the user's score band; bands without one show no bubble. The
  // severity copy moves into the score card.
  withZiggie?: {
    gif: GifName
    messages: Partial<Record<E7ScoreCategory, string>>
    typewriter?: boolean
  }
}

type Props = SymptomScoreResultsScreenE7Props & {
  onNextClick: onNextClickType
  quiz: QuizBase
}

export const SymptomScoreResultsScreenE7: React.FC<Props> = ({
  buttonText = 'Continue',
  withZiggie,
  compactKeySymptoms,
  onNextClick,
  quiz,
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const location = useLocation()
  const { quizVersion } = getQuizRouteConfig(location.pathname)
  const productType = DEFAULT_QUIZ_CONFIG[quizVersion]?.productType

  const score = calculateE7SymptomScore(quiz)
  const category = getE7ScoreCategory(score)
  const topSymptoms = getE7TopSymptoms(quiz)
  const ziggieMessage = withZiggie?.messages[category]

  // Count the number up from 0 → score and reveal the key symptoms only once the
  // score has finished animating (per the Figma SCORE ANIMATION note).
  const [displayScore, setDisplayScore] = useState(0)
  const [barFraction, setBarFraction] = useState(0)
  const [symptomsRevealed, setSymptomsRevealed] = useState(false)
  const startRef = useRef<number | null>(null)
  const hasSubmittedRef = useRef(false)

  useEffect(() => {
    // Reset the animation start timestamp so the count-up replays from 0 if the
    // score changes (e.g. when the quiz state rehydrates after a refresh).
    startRef.current = null
    // Kick the bar transition on the next frame so the CSS transition runs.
    const raf = requestAnimationFrame(() => setBarFraction(Math.min(1, Math.max(0, score / 100))))

    let frame: number
    const tick = (now: number) => {
      if (startRef.current === null) startRef.current = now
      const progress = Math.min(1, (now - startRef.current) / ANIMATION_MS)
      setDisplayScore(Math.round(progress * score))
      if (progress < 1) {
        frame = requestAnimationFrame(tick)
      } else {
        setSymptomsRevealed(true)
      }
    }
    frame = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(raf)
      cancelAnimationFrame(frame)
    }
  }, [score])

  // Persist the derived values and fire one quiz_question_answered per derived
  // answer, mirroring the e5 results screen. Submits exactly once, and only once
  // the quiz state is populated — guarding against an empty submission if Redux
  // rehydrates asynchronously (e.g. on a hard refresh of this screen).
  useEffect(() => {
    if (hasSubmittedRef.current) return
    if (Object.keys(quiz).length === 0) return
    hasSubmittedRef.current = true

    sessionStorage.setItem(VISITED_SYMPTOM_RESULTS_KEY, 'true')
    const keySymptoms = topSymptoms.map((s) => s.key)
    const answers: Answer[] = [
      { question_key: 'symptom_score', answer: String(score) },
      { question_key: 'symptom_score_category', answer: category },
      { question_key: 'symptom_score_key_symptoms', answer: keySymptoms },
    ]
    dispatch(submitAnswers({ quiz_version: quizVersion, answers }))
    answers.forEach((a) => {
      const value = Array.isArray(a.answer) ? a.answer.toString() : a.answer
      trackQuizQuestionAnswered(quizVersion, productType, a.question_key, value)
    })
  }, [quiz, score, category, topSymptoms, dispatch, quizVersion, productType])

  return (
    <>
      <ScreenContainer>
        <Card>
          <DSText variant="fluid-paragraph-300" weight="regular" color="secondary">
            Your symptom severity is
          </DSText>
          <DSSpacer size={8} direction="vertical" />
          <DSText variant="fluid-display-1000" weight="semiBold">
            {displayScore}
          </DSText>
          <DSSpacer size={4} direction="vertical" />
          <DSText variant="fluid-paragraph-300" weight="regular" color="secondary">
            out of 100
          </DSText>
          <DSSpacer size={24} direction="vertical" />
          <Bar>
            <BarFill $fraction={barFraction} />
          </Bar>
          <DSSpacer size={8} direction="vertical" />
          <BarLabelsRow>
            <DSText variant="fluid-label-100" weight="regular">
              Moderate
            </DSText>
            <DSText variant="fluid-label-100" weight="regular">
              Severe
            </DSText>
          </BarLabelsRow>
          {withZiggie && (
            <>
              <DSSpacer size={16} direction="vertical" />
              <DSText variant="fluid-paragraph-200" weight="regular" align="left" style={{ width: '100%' }}>
                <ContextAwareText text={CATEGORY_BODY[category]} quiz={quiz} />
              </DSText>
            </>
          )}
        </Card>
        <DSSpacer size={24} direction="vertical" />
        {withZiggie ? (
          ziggieMessage && (
            <ZiggieAsk
              gif={withZiggie.gif}
              text={ziggieMessage}
              quiz={quiz}
              typewriter={withZiggie.typewriter}
              compact
            />
          )
        ) : (
          <DSText variant="fluid-paragraph-300" weight="regular">
            <ContextAwareText text={CATEGORY_BODY[category]} quiz={quiz} />
          </DSText>
        )}
        {/* Ziggie's 108px row already carries its own padding. */}
        <DSSpacer size={withZiggie ? 12 : 32} direction="vertical" />
        <DSText variant={compactKeySymptoms ? 'fluid-paragraph-300' : 'fluid-heading-500'} weight="semiBold">
          Your key symptoms
        </DSText>
        <DSSpacer size={16} direction="vertical" />
        <div style={{ opacity: symptomsRevealed ? 1 : 0, transition: 'opacity 300ms ease-out' }}>
          {topSymptoms.map((symptom) => {
            const colours = SEVERITY_COLOURS[symptom.severity]
            return (
              <React.Fragment key={symptom.key}>
                <SymptomCard>
                  <SymptomHeaderRow>
                    <DSText
                      variant={compactKeySymptoms ? 'fluid-paragraph-200' : 'fluid-paragraph-300'}
                      weight="semiBold"
                    >
                      {symptom.label}
                    </DSText>
                    <SeverityBadge $background={colours.background} $color={colours.color}>
                      {symptom.severity}
                    </SeverityBadge>
                  </SymptomHeaderRow>
                  {symptom.description && (
                    <>
                      <CardDivider />
                      <DSText variant="fluid-paragraph-200" weight="regular" color="secondary">
                        {symptom.description}
                      </DSText>
                    </>
                  )}
                </SymptomCard>
                <DSSpacer size={12} direction="vertical" />
              </React.Fragment>
            )
          })}
        </div>
        <DSSpacer size={16} direction="vertical" />
        <DSText variant="fluid-label-100" weight="regular" align="center" color="placeholder">
          {DISCLAIMER}
        </DSText>
      </ScreenContainer>
      <StickyFooter>
        <StickyFooterContent>
          <DSButton
            variant="primary"
            colorScheme="default"
            size="large"
            width="full"
            onPress={() => (onNextClick as () => void)()}
            testId="continue-button"
          >
            <DSText variant="fluid-paragraph-200" weight="semiBold">
              {buttonText}
            </DSText>
          </DSButton>
        </StickyFooterContent>
      </StickyFooter>
    </>
  )
}
